import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, AlertTriangle, Droplets, Upload, Printer,
} from 'lucide-react';
import { CalcShadowProvider } from '@/components/CalcShadow';
import GutterBuilderPanel from '@/components/GutterBuilderPanel';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';

/**
 * GutteringBuilderTab — Sprint 2 Order 3 (rewrite).
 *
 * The legacy flat-rate `guttering_builder` backend was retired on
 * 2026-09-14 as part of the pricing-pipeline consolidation. All
 * guttering work now flows through the pin-based `gutter_builder`
 * (see `GutterBuilderPanel` below). This tab is the shell + push
 * button; the real editor lives in the child panel.
 */
export default function GutteringBuilderTab({ api, appraisalId }) {
  const [pushing, setPushing] = useState(false);
  const [flash, setFlash]     = useState(null);
  // Bumped whenever a push lands so GutterBuilderPanel can reload its
  // computed lines / totals (its own preview also runs live but the
  // reload is instant feedback that "these are the numbers we saved").
  const [reloadTick, setReloadTick] = useState(0);

  const pushToComponents = async () => {
    if (!window.confirm('Push the active gutter-builder lines onto the Components tab? Existing gutter_builder lines on this appraisal will be replaced.')) return;
    setPushing(true);
    setFlash(null);
    try {
      const { data } = await api.post(`/guttering/appraisals/${appraisalId}/gutter-builder/push`);
      const grand = Number(data?.grand_total || 0).toFixed(2);
      const unpriced = Number(data?.sync?.unpriced_count || 0);
      const unpricedNote = unpriced > 0 ? ` · ${unpriced} unpriced line${unpriced === 1 ? '' : 's'}` : '';
      setFlash({
        tone: 'ok',
        msg: `Pushed ${data.inserted} guttering line${data.inserted === 1 ? '' : 's'} (grand total $${grand})${unpricedNote}`,
      });
      toast.success(`Guttering pushed · $${grand}`);
      setReloadTick((n) => n + 1);
    } catch (e) {
      // Wave 3 M7 (2026-02) — backend now returns a structured 409
      // when unmatched pins block the push. Extract the message from
      // the object detail so we don't render `[object Object]`.
      const raw = e?.response?.data?.detail;
      const detail = typeof raw === 'object' && raw !== null
        ? (raw.message || raw.code || JSON.stringify(raw))
        : (raw || e.message);
      setFlash({ tone: 'error', msg: detail });
      toast.error(detail);
    } finally {
      setPushing(false);
    }
  };

  return (
    <CalcShadowProvider defaultShow={true}>
      <div className="space-y-5" data-testid="guttering-builder-tab">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
              <Droplets size={18} className="text-sky-600" /> Guttering Builder
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Pin-based per-run pricing. Add spouting pins on the Diagram tab, then set profile / material below. Push commits the priced lines to the master quote.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="guttering-push"
              onClick={pushToComponents}
              disabled={pushing}
              title="Push priced gutter-builder lines to the Components tab"
              className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {pushing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Push to Components
            </button>
            <button
              data-testid="guttering-pdf"
              onClick={() => window.open(`${api.defaults.baseURL}/guttering/appraisals/${appraisalId}/gutter-builder/quote.pdf`, '_blank')}
              title="Open the one-page pin-based Gutter Builder quote PDF"
              className="bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 text-sm font-semibold rounded-md px-3 py-1.5 inline-flex items-center gap-1"
            >
              <Printer size={13} /> Guttering Quote PDF
            </button>
          </div>
        </div>

        {/* Roof Details quick-edit panel under the banner. */}
        <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={() => setReloadTick((n) => n + 1)} />

        {flash && (
          <div data-testid="guttering-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
            {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
            {flash.msg}
          </div>
        )}

        {/* The actual pin-based editor. `reloadKey` forces a full remount
            on push so the panel's `useEffect` re-reads config from the
            server. */}
        <GutterBuilderPanel
          api={api}
          appraisalId={appraisalId}
          key={`gbp-${reloadTick}`}
        />
      </div>
    </CalcShadowProvider>
  );
}
